import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AnyObject, FieldOption } from '@mri/types';
import { GenericResourceService } from '@mri/app/resources';
import { ConcatArrayPipe } from '../../pipes';
import { MatOptionModule } from '@angular/material/core';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { finalize, mergeMap, Observable, of, Subject, Subscription, tap, filter } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NgxVisibilityModule } from 'ngx-visibility';
import { CardComponent } from '../../@theme/components/card/card.component';

enum ModeEnum {
  OPTIONS = 'options',
  CARD = 'card',
}

@Component({
  selector: 'app-simple-list',
  standalone: true,
  imports: [ConcatArrayPipe, MatOptionModule, MatCardModule, MatChipsModule, NgxVisibilityModule, CardComponent],
  templateUrl: './simple-list.component.html',
  styleUrl: './simple-list.component.scss'
})
export class SimpleListComponent implements OnInit, OnDestroy {
  private _subscription = new Subscription();
  private _search$ = new Subject<{ search: string; page: number }>();
  private _currentPage: number = 1;
  private _currentSearch: string = '';
  private _selectedValues = new Set<string>();
  private _isSearching = false;
  private _isEnd = false;
  protected readonly ModeEnum = ModeEnum;

  dynamicElements: FieldOption[] = [];
  isLoading: boolean;
  staticElementList: FieldOption[] = [];
  dynamicElementList: FieldOption[] = [];


  @Input() elementsUrl: string;
  @Input() elements?: FieldOption[];
  @Input() mode: 'options'| 'card' = ModeEnum.CARD;
  @Input() size: number = 10;
  @Input() multiselect: boolean = false;
  @Input() filter?: Record<string, string | string[]>;

  @Output() valueChange = new EventEmitter<Array<string> | string>();

  @Input() set search(search: string) {
    this._search$.next({ search, page: 1 });
  }

  @Input() set value(value: string | Array<string>) {
    if (Array.isArray(value)) this._selectedValues = new Set(value);
    else this._selectedValues.add(value);
  }

  constructor(
    private _genericSrv: GenericResourceService
  ) {
    this._subscription.add(
      this._search$.pipe(
        filter(() => !this._isSearching),
        tap(() => this._isSearching = true),
        tap(data => {
          this._currentPage = data.page;
          this._currentSearch = data.search;
        }),
        tap(() => this._filterStaticElements()),
        mergeMap(() => this._consultDynamicElements()),
        tap(() =>  this._isSearching = false)
      ).subscribe()
    );
  }

  ngOnInit() {
    console.log('Init: ', this.elementsUrl);
    this._search$.next({ search: '', page: 1 });
  }

  ngOnDestroy() {
    this._subscription.unsubscribe();
  }

  onClick(option: FieldOption) {
    const { value } = option;
    if (this._selectedValues.has(value))
      this._selectedValues.delete(value);
    else
      this._selectedValues.add(value);

    this.valueChange.next([...this._selectedValues]);
  }


  checkVisible(visible: boolean, index: number) {
    if(!visible) return;

    const size = [...(this.staticElementList || []), ...(this.dynamicElementList || [])].length;

    if((index + 3) >= size) this._nextPage()
  }

  private _nextPage() {
    this._search$.next({ page: this._currentPage + 1, search: this._currentSearch });
  }

  private _filterStaticElements() {
    const { elements, _currentSearch } = this;
    const regex = new RegExp((_currentSearch || '').toString(), 'gi');
    this.staticElementList = elements?.filter(o => regex.test(o?.label)) || [];
  }

  private _consultDynamicElements(search: string = this._currentSearch): Observable<AnyObject> {
    if (!this.elementsUrl) return of(null);

    const optionsUrl = this.elementsUrl;

    this.isLoading = true;
    return this._genericSrv
      .getElements<FieldOption>(optionsUrl, {
        filter: { search, ...(this.filter || {}) },
        paginate: { page: this._currentPage, size: this.size }
      })
      .pipe(
        catchError(() => of({ objects: [] })),
        tap(({ objects }) => {
          if (this._currentPage === 1)
            return (this.dynamicElementList = objects || []) as unknown as void;

          this.dynamicElementList = [...this.dynamicElementList, ...objects];
        }),
        finalize(() => this.isLoading = false)
      );
  }
}
